import './styles.scss';
import { useState } from "react";
import { Button } from "@fluentui/react-components";
import { showToast } from "#root/components/GlobalToaster";
import {
  Incident,
  useIncidents,
  useCreateIncident,
  useUpdateIncident,
  useDeleteIncident,
  useInfiniteIncidents,
  useIncidentById
} from "#root/queries/incidents";
import { QRCodeDialog } from "#root/components/QRCodeDialog";
import { IncidentDialog } from "#root/components/IncidentDialog";
import { DialogBox } from "#root/components/DialogBox";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import ActionCards from "#root/components/ActionCards";
import { useInfiniteDropdown } from "#root/hooks/useInfiniteDropdown";
import { useCurrentTenant, useCurrentUser } from "#root/queries/local";
import { Add16Filled } from "@fluentui/react-icons";
import { CreateReportDialog } from "#root/components/CreateReportDialog";
import { useCreateAfterActionReport } from "#root/queries/afterActionReport";
import IncidentList from "#root/components/IncidentList";
import { ROLES } from "#root/utils/roles";
const ITEMS_PER_PAGE = 10;

export interface ActionCard {
  image: string;
  title: string;
  onClick: () => void;
}

export interface IncidentActionHandlers {
  onEdit: (incident: Incident, e: React.MouseEvent) => void;
  onDelete: (incident: Incident, e: React.MouseEvent) => void;
  onQRCode: (incident: Incident, e: React.MouseEvent) => void;
  onCreateReport: (incident: Incident, e: React.MouseEvent) => void;
}

export interface BaseIncidentsListProps {
  className?: string;
  initialIncidentId?: string;
  onIncidentChange?: (id: string) => void;
  actionCards: ActionCard[];
  renderActions: (incident: Incident, handlers: IncidentActionHandlers) => React.ReactNode;
  generateQrUrl: (tenantId?: string, incidentId?: string) => string;
  extraHeaderActions?: React.ReactNode;
  children?: React.ReactNode;
}

const BaseIncidentsList: React.FC<BaseIncidentsListProps> = ({
  className,
  initialIncidentId = '',
  onIncidentChange,
  actionCards,
  renderActions,
  generateQrUrl,
  extraHeaderActions,
  children
}) => {
  const { data: tenantId } = useCurrentTenant();
  const { data: currentUser } = useCurrentUser();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | undefined>(undefined);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [incidentToDelete, setIncidentToDelete] = useState<Incident | null>(null);
  const [isQRDialogOpen, setIsQRDialogOpen] = useState(false);
  const [incidentForQR, setIncidentForQR] = useState<Incident | null>(null);
  const [selectedIncidentForReport, setSelectedIncidentForReport] = useState<Incident | null>(null);
  const [isCreateReportDialogOpen, setIsCreateReportDialogOpen] = useState(false);

  const [activePage, setActivePage] = useState(0);
  const [inactivePage, setInactivePage] = useState(0);

  const createIncidentMutation = useCreateIncident();
  const updateIncidentMutation = useUpdateIncident();
  const deleteIncidentMutation = useDeleteIncident();
  const createReportMutation = useCreateAfterActionReport();

  const {
    selectedId: selectedIncidentId,
    selectedData: selectedIncidentData,
    handleChange: handleIncidentChange,
    setFilter: setIncidentFilter,
    queryResult: incidentQueryResult
  } = useInfiniteDropdown<Incident>({
    initialId: initialIncidentId,
    onChange: (id: string | null) => {
      if (onIncidentChange && id) {
        onIncidentChange(id);
      }
    },
    useInfiniteQuery: useInfiniteIncidents,
    useItemById: useIncidentById
  });

  // Fetch active incidents
  const {
    data: activeIncidentsResponse,
    isLoading: activeLoading
  } = useIncidents({
    isActive: true,
    skip: activePage * ITEMS_PER_PAGE,
    take: ITEMS_PER_PAGE
  });

  // Fetch inactive incidents
  const {
    data: inactiveIncidentsResponse,
    isLoading: inactiveLoading
  } = useIncidents({
    isActive: false,
    skip: inactivePage * ITEMS_PER_PAGE,
    take: ITEMS_PER_PAGE
  });

  const activeIncidents = activeIncidentsResponse?.items ?? [];
  const inactiveIncidents = inactiveIncidentsResponse?.items ?? [];

  // After Action Report
  const handleCreateReportClick = (incident: Incident, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIncidentForReport(incident);
    setIsCreateReportDialogOpen(true);
  };

  const handleCreateReport = async (reportData: {
    reportName: string;
    startDate: Date;
    endDate: Date;
    improvementPlanTable: boolean;
  }) => {
    if (!selectedIncidentForReport?.id) {
      return;
    }
    if (!currentUser) {
      return;
    }

    try {
      console.log(currentUser.role?.toUpperCase());
      await createReportMutation.mutateAsync({
        report: {
          ...reportData,
          incidentId: selectedIncidentForReport.id,
          userId: currentUser.id,
          isSuperAdmin: currentUser.role?.toUpperCase() === ROLES.GLOBALADMIN,
        }
      });
      showToast("Report created successfully", "success");
      setIsCreateReportDialogOpen(false);
    } catch (error) {
      showToast("Failed to create a report", "error");
    }
  };

  // Create incident
  const handleCreateIncident = async (incident: Partial<Incident>) => {
    try {
      await createIncidentMutation.mutateAsync({ incident });
      showToast("Incident created successfully", "success");
      setIsCreateDialogOpen(false);
    } catch (error) {
      showToast("Failed to create incident", "error");
    }
  };

  // Update incident
  const onEdit = (incident: Incident, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIncident(incident);
    setIsEditDialogOpen(true);
  };

  const handleUpdateIncident = async (incident: Partial<Incident>) => {
    if (!selectedIncident?.id) {
      return;
    }

    try {
      await updateIncidentMutation.mutateAsync({
        id: selectedIncident.id,
        incident
      });
      showToast("Incident updated successfully", "success");
      setIsEditDialogOpen(false);
    } catch (error) {
      showToast("Failed to update incident", "error");
    }
  };

  // Delete incident
  const handleDeleteClick = (incident: Incident, e: React.MouseEvent) => {
    e.stopPropagation();
    setIncidentToDelete(incident);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!incidentToDelete?.id) {
      return;
    }

    try {
      // The deleteIncidentMutation expects a string ID directly
      await deleteIncidentMutation.mutateAsync(incidentToDelete.id);
      showToast("Incident deleted successfully", "success");
      setIsDeleteDialogOpen(false);
    } catch (error) {
      showToast("Failed to delete incident", "error");
    }
  };

  const handleQRClick = (incident: Incident, e: React.MouseEvent) => {
    e.stopPropagation();
    setIncidentForQR(incident);
    setIsQRDialogOpen(true);
  };

  // Create action handlers object to pass to renderActions
  const actionHandlers: IncidentActionHandlers = {
    onEdit,
    onDelete: handleDeleteClick,
    onQRCode: handleQRClick,
    onCreateReport: handleCreateReportClick
  };

  return (
    <div className={`incidents-list-container ${className}`}>
      <div>
        <h2>
          Select the Incident <div className="divider" />
        </h2>
        <div className="incident-dropdown">
          <InfiniteDropdown
            value={selectedIncidentId ?? ''}
            onChange={(_, data) => handleIncidentChange(data.value)}
            onFilterChange={setIncidentFilter}
            queryResult={incidentQueryResult}
            itemToOption={(incident) => ({
              value: incident.id ?? '',
              text: incident.name ?? ''
            })}
            placeholder="Select incident"
            selectedItem={selectedIncidentData}
          />
        </div>
      </div>

      <ActionCards cards={actionCards} />

      <IncidentList
        title="Active Incidents"
        incidents={activeIncidents}
        isLoading={activeLoading}
        page={activePage}
        onPageChange={setActivePage}
        totalItems={activeIncidentsResponse?.totalCount ?? 0}
        itemsPerPage={ITEMS_PER_PAGE}
        renderActions={(incident) => renderActions(incident, actionHandlers)}
        headerActions={
          <>
            <Button
              appearance="primary"
              icon={<Add16Filled />}
              onClick={() => setIsCreateDialogOpen(true)}
            >
              New Incident
            </Button>
            {extraHeaderActions}
          </>
        }
      />

      <IncidentList
        title="Inactive Incidents"
        incidents={inactiveIncidents}
        isLoading={inactiveLoading}
        page={inactivePage}
        onPageChange={setInactivePage}
        totalItems={inactiveIncidentsResponse?.totalCount ?? 0}
        itemsPerPage={ITEMS_PER_PAGE}
        renderActions={(incident) => renderActions(incident, actionHandlers)}
      />

      {children}

      <IncidentDialog
        open={isCreateDialogOpen}
        onOpenChange={(_, data) => setIsCreateDialogOpen(data.open)}
        onSave={handleCreateIncident}
        title="New Incident"
      />

      <IncidentDialog
        open={isEditDialogOpen}
        onOpenChange={(_, data) => setIsEditDialogOpen(data.open)}
        onSave={handleUpdateIncident}
        incident={selectedIncident}
        title="Edit Incident"
      />

      <DialogBox
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete Incident"
        primaryButtonText="Delete"
        onPrimaryButtonClick={handleConfirmDelete}
        isPrimaryButtonLoading={deleteIncidentMutation.isPending}
      >
        {incidentToDelete && <>Are you sure you want to delete incident {incidentToDelete.name}?</>}
      </DialogBox>

      <QRCodeDialog
        incident={incidentForQR}
        open={isQRDialogOpen}
        onOpenChange={setIsQRDialogOpen}
        qrUrl={generateQrUrl(tenantId || undefined, incidentForQR?.id || undefined)}
      />

      <CreateReportDialog
        open={isCreateReportDialogOpen}
        onOpenChange={setIsCreateReportDialogOpen}
        onCreate={handleCreateReport}
      />
    </div>
  );
};

export default BaseIncidentsList;
